import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SellerLayout } from '../components/SellerLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { colors } from '../constants/colors';
import { getShow, getSalesByShow, getOrdersByShow, downloadBulkPickLists } from '../api/client';
import { parseUTC } from '../utils/date';
import type { Show, ItemSaleWithPayment, Order, OrderStatus } from '../types';
import { ArrowLeft, ShoppingBag, DollarSign, Clock, Truck, Package, Check, Download, Loader2, X, AlertTriangle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const SHOW_STATUS_CONFIG: Record<Show['status'], { label: string; color: string; bgColor: string }> = {
  SCHEDULED: { label: 'Scheduled', color: '#3B82F6', bgColor: '#DBEAFE' },
  LIVE: { label: 'Live', color: '#DC2626', bgColor: '#FEE2E2' },
  ENDED: { label: 'Ended', color: colors.gray[600], bgColor: colors.gray[100] },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100] },
};

const ORDER_STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bgColor: string; Icon: LucideIcon }> = {
  OPEN: { label: 'Open', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  UNSHIPPED: { label: 'Unshipped', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  PARTIALLY_SHIPPED: { label: 'Partially Shipped', color: '#F97316', bgColor: '#FFF7ED', Icon: Package },
  LABEL_CREATED: { label: 'Label Created', color: '#5856D6', bgColor: '#EDE9FE', Icon: Package },
  SHIPPED: { label: 'Shipped', color: '#007AFF', bgColor: '#DBEAFE', Icon: Truck },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7', Icon: Check },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
};

const ORDERS_GRID = '1.5fr 0.8fr 1fr 1.2fr 1.5fr';

function formatDateTime(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function formatDuration(startedAt: string | null, endedAt: string | null): string {
  if (!startedAt || !endedAt) return '--';
  const start = parseUTC(startedAt);
  const end = parseUTC(endedAt);
  const diffMs = end.getTime() - start.getTime();
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export function SellerShowDetailPage() {
  const { showId } = useParams<{ showId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [show, setShow] = useState<Show | null>(null);
  const [sales, setSales] = useState<ItemSaleWithPayment[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  useDocumentTitle(show?.name ?? 'Show Detail');

  useEffect(() => {
    if (showId) {
      loadData();
    }
  }, [showId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [showData, salesData, ordersData] = await Promise.all([
        getShow(showId!),
        getSalesByShow(showId!).catch(() => []),
        getOrdersByShow(showId!).catch(() => []),
      ]);
      setShow(showData);
      // Sort sales newest first
      salesData.sort((a, b) => parseUTC(b.createdAt).getTime() - parseUTC(a.createdAt).getTime());
      setSales(salesData);
      setOrders(ordersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load show');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintAllPickLists = async () => {
    if (orders.length === 0) return;
    try {
      setIsDownloading(true);
      const blob = await downloadBulkPickLists({
        orderIds: orders.map(o => o.id),
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      console.error('Failed to download pick lists:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <SellerLayout>
        <div style={styles.loading}>Loading show...</div>
      </SellerLayout>
    );
  }

  if (error || !show) {
    return (
      <SellerLayout>
        <div style={styles.error}>{error || 'Show not found'}</div>
      </SellerLayout>
    );
  }

  const statusConfig = SHOW_STATUS_CONFIG[show.status];
  const totalVolume = sales.reduce((sum, o) => sum + o.total, 0);
  const totalShipping = sales.reduce((sum, o) => sum + (o.shippingCost || 0), 0);

  return (
    <SellerLayout>
      {/* Back button + header */}
      <div style={styles.header}>
        <button onClick={() => navigate('/sell')} style={styles.backButton}>
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
      </div>

      {/* Show info */}
      <div style={styles.showHeader}>
        <div style={styles.showTitleRow}>
          <h1 style={styles.showName}>{show.name}</h1>
          <span style={{
            ...styles.statusBadge,
            color: statusConfig.color,
            backgroundColor: statusConfig.bgColor,
          }}>
            {statusConfig.label}
          </span>
        </div>
        <div style={styles.showMeta}>
          <span>{show.scheduledAt ? formatDateTime(show.scheduledAt) : '--'}</span>
          {show.categories && show.categories.length > 0 && (
            <>
              <span style={styles.metaSep}>&middot;</span>
              <span>{show.categories.join(', ')}</span>
            </>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ ...styles.statsGrid, gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)' }}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#DBEAFE' }}>
            <ShoppingBag size={18} color="#3B82F6" />
          </div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Items Sold</span>
            <span style={styles.statValue}>{sales.length}</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#F3E8FF' }}>
            <Package size={18} color="#8B5CF6" />
          </div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Orders</span>
            <span style={styles.statValue}>{orders.length}</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#DCFCE7' }}>
            <DollarSign size={18} color="#22C55E" />
          </div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Volume</span>
            <span style={styles.statValue}>{formatCurrency(totalVolume)}</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#FEF3C7' }}>
            <Clock size={18} color="#F59E0B" />
          </div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Duration</span>
            <span style={styles.statValue}>{formatDuration(show.startedAt, show.endedAt)}</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#EDE9FE' }}>
            <Truck size={18} color="#5856D6" />
          </div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Shipping Collected</span>
            <span style={styles.statValue}>{formatCurrency(totalShipping)}</span>
          </div>
        </div>
      </div>

      {/* Orders header + Print button */}
      <div style={styles.tabBarRow}>
        <h2 style={styles.sectionTitle}>Orders</h2>
        {orders.length > 0 && (
          <button
            onClick={handlePrintAllPickLists}
            style={styles.printButton}
            disabled={isDownloading}
          >
            {isDownloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            <span>{isDownloading ? 'Generating...' : 'Print All Pick Lists'}</span>
          </button>
        )}
      </div>

      {/* Orders table */}
      <div style={{ overflowX: isMobile ? 'auto' : undefined, WebkitOverflowScrolling: 'touch' }}>
      <div style={{ ...styles.tableWrapper, minWidth: isMobile ? '800px' : undefined }}>
        {orders.length === 0 ? (
          <div style={styles.emptyState}>No orders for this show</div>
        ) : (
          <>
            <div style={{ ...styles.tableHeader, gridTemplateColumns: ORDERS_GRID }}>
              <span>Recipient</span>
              <span>Items</span>
              <span>Value</span>
              <span>Status</span>
              <span>Tracking</span>
            </div>
            {orders.map((order) => {
              const orderConfig = ORDER_STATUS_CONFIG[order.status];
              return (
                <div
                  key={order.id}
                  style={{ ...styles.tableRow, gridTemplateColumns: ORDERS_GRID }}
                  onClick={() => navigate(`/sell/orders/${order.id}`)}
                >
                  <span>
                    <span style={styles.cellTextBold}>{order.recipientName}</span>
                    <span style={styles.cellSubtext}>{order.buyerUsername || ''}</span>
                  </span>
                  <span style={styles.cellText}>{order.itemSaleCount} item{order.itemSaleCount !== 1 ? 's' : ''}</span>
                  <span style={styles.cellTextBold}>{formatCurrency(order.totalValue)}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      ...styles.statusBadge,
                      color: orderConfig.color,
                      backgroundColor: orderConfig.bgColor,
                    }}>
                      {orderConfig.label}
                    </span>
                    {order.hasFailedPayments && (
                      <AlertTriangle size={16} color="#F59E0B" />
                    )}
                  </span>
                  <span>
                    {order.shipments && order.shipments.length > 0 && order.shipments[0].trackingNumber ? (
                      <span style={styles.trackingText}>
                        {order.shipments[0].carrier} {order.shipments[0].trackingNumber}
                      </span>
                    ) : (
                      <span style={styles.cellSubtext}>--</span>
                    )}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </div>
      </div>
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loading: {
    padding: '48px',
    textAlign: 'center',
    color: colors.gray[600],
    fontSize: '14px',
  },
  error: {
    padding: '48px',
    textAlign: 'center',
    color: colors.status.error,
    fontSize: '14px',
  },
  header: {
    marginBottom: '16px',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: 'none',
    color: colors.brand.blue,
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '4px 0',
  },
  showHeader: {
    marginBottom: '24px',
  },
  showTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  showName: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: 0,
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
  },
  showMeta: {
    fontSize: '14px',
    color: colors.gray[600],
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  metaSep: {
    color: colors.gray[400],
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '12px',
    marginBottom: '24px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[200]}`,
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  statLabel: {
    fontSize: '12px',
    color: colors.gray[600],
  },
  statValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  tabBarRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: 0,
  },
  printButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: colors.brand.blue,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  tableWrapper: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid',
    gap: '16px',
    padding: '12px 16px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px 8px 0 0',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'grid',
    gap: '16px',
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.gray[200]}`,
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  cellText: {
    color: colors.gray[700],
  },
  cellTextBold: {
    fontWeight: 600,
    color: colors.ink.black,
  },
  cellSubtext: {
    fontSize: '12px',
    color: colors.gray[400],
    display: 'block',
  },
  miniStatus: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
  },
  trackingText: {
    fontSize: '13px',
    color: colors.gray[700],
    fontFamily: 'monospace',
  },
  emptyState: {
    padding: '48px',
    textAlign: 'center',
    color: colors.gray[600],
    fontSize: '14px',
  },
};
